/* ==========================================================================
   FLC Forge — Global JS
   ========================================================================== */

'use strict';

/* Utility helpers
   ========================================================================== */
const FLC = {
  /** Debounce a function */
  debounce(fn, delay = 250) {
    let timer;
    return (...args) => {
      clearTimeout(timer);
      timer = setTimeout(() => fn(...args), delay);
    };
  },

  /** Throttle a function */
  throttle(fn, limit = 100) {
    let lastRun = 0;
    return (...args) => {
      const now = Date.now();
      if (now - lastRun >= limit) { lastRun = now; fn(...args); }
    };
  },

  /** Format money using Shopify money format */
  formatMoney(cents, format = window.flcForge.moneyFormat) {
    if (typeof cents === 'string') cents = cents.replace('.', '');
    const value = parseInt(cents, 10) / 100;
    const withCommas = (n) => n.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    const amount         = withCommas(value.toFixed(2));
    const amountNoDecimal = withCommas(String(Math.round(value)));
    const amountCommaSep = value.toFixed(2).replace('.', ',');
    return format
      .replace('{{amount}}',                    amount)
      .replace('{{amount_no_decimals}}',         amountNoDecimal)
      .replace('{{amount_with_comma_separator}}', amountCommaSep);
  },

  /** Format money without cents using Shopify money format */
  formatMoneyNoCents(cents, format = window.flcForge.moneyFormat) {
    if (typeof cents === 'string') cents = cents.replace('.', '');
    const value = parseInt(cents, 10) / 100;
    const withCommas = (n) => n.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    const amountNoDecimals = withCommas(String(Math.floor(value)));
    return format.replace(/\{\{amount[^}]*\}\}/g, amountNoDecimals);
  },

  /** Fetch wrapper with JSON */
  async fetchJSON(url, options = {}) {
    const res = await fetch(url, {
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      ...options
    });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return res.json();
  },

  /** Trap focus within element */
  trapFocus(element) {
    const focusableEls = element.querySelectorAll(
      'a[href], button:not([disabled]), input:not([disabled]), select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    const first = focusableEls[0];
    const last  = focusableEls[focusableEls.length - 1];

    function onKeyDown(e) {
      if (e.key !== 'Tab') return;
      if (e.shiftKey) {
        if (document.activeElement === first) { e.preventDefault(); last.focus(); }
      } else {
        if (document.activeElement === last) { e.preventDefault(); first.focus(); }
      }
    }

    element.addEventListener('keydown', onKeyDown);
    first && first.focus();
    return () => element.removeEventListener('keydown', onKeyDown);
  },

  /** Remove focus trap */
  removeTrapFocus(element) {
    element.removeEventListener('keydown', () => {});
  },

  /** Open overlay */
  openOverlay() {
    document.getElementById('overlay')?.classList.add('is-visible');
    document.body.style.overflow = 'hidden';
  },

  /** Close overlay */
  closeOverlay() {
    document.getElementById('overlay')?.classList.remove('is-visible');
    document.body.style.overflow = '';
  },

  /** Dispatch custom event */
  dispatch(name, detail = {}, target = document) {
    target.dispatchEvent(new CustomEvent(name, { bubbles: true, cancelable: true, detail }));
  }
};

window.FLC = FLC;

/* Overlay click → close all drawers
   ========================================================================== */
document.getElementById('overlay')?.addEventListener('click', () => {
  FLC.dispatch('flc:drawers:close-all');
});

/* Announce to screen readers
   ========================================================================== */
class LiveRegion {
  constructor() {
    this.el = Object.assign(document.createElement('p'), {
      className: 'visually-hidden',
      'aria-live': 'polite',
      'aria-atomic': 'true'
    });
    document.body.appendChild(this.el);
  }

  announce(msg) {
    this.el.textContent = '';
    requestAnimationFrame(() => { this.el.textContent = msg; });
  }
}

window.liveRegion = new LiveRegion();

/* Header height CSS var
   ========================================================================== */
function setHeaderHeight({ updateGalleryHeight = false } = {}) {
  const header = document.querySelector('.site-header');
  const announcementBar = document.querySelector('.announcement-bar');
  const isFixedHeader = header?.dataset.headerPosition === 'fixed';
  const isAnnouncementVisible = !!(isFixedHeader && announcementBar && !announcementBar.classList.contains('is-hidden'));
  const headerHeight = header ? header.offsetHeight : 0;
  const announcementHeight = isAnnouncementVisible ? announcementBar.offsetHeight : 0;
  const totalOffset = headerHeight + announcementHeight;

  document.documentElement.style.setProperty('--header-height', `${headerHeight}px`);
  document.documentElement.style.setProperty('--announcement-height', `${announcementHeight}px`);
  document.documentElement.style.setProperty('--header-top', `${announcementHeight}px`);
  document.documentElement.style.setProperty('--header-offset', `${totalOffset}px`);

  // --gallery-height is set once on load (full header + announcement visible) so the
  // grid_snap gallery doesn't resize mid-scroll when the announcement bar hides.
  if (updateGalleryHeight) {
    document.documentElement.style.setProperty('--gallery-height', `calc(100vh - ${totalOffset}px)`);
  }
}

const resizeObserver = new ResizeObserver(FLC.debounce(() => setHeaderHeight({ updateGalleryHeight: true }), 100));
const header = document.querySelector('.site-header');
const announcementBar = document.querySelector('.announcement-bar');
if (header) { resizeObserver.observe(header); }
if (announcementBar) { resizeObserver.observe(announcementBar); }
setHeaderHeight({ updateGalleryHeight: true });

/* Sticky header
   ========================================================================== */
(function initStickyHeader() {
  const header = document.querySelector('.site-header--sticky');
  if (!header) return;
  const announcementBar = document.querySelector('.announcement-bar');
  const isFixedHeader = header.dataset.headerPosition === 'fixed';

  if (isFixedHeader && announcementBar) {
    announcementBar.classList.add('announcement-bar--fixed');
  }

  const syncHeaderState = () => {
    const currentScrollY = window.scrollY;
    const isScrolled = currentScrollY > 80;

    header.classList.toggle('is-scrolled', isScrolled);

    if (isFixedHeader && announcementBar) {
      announcementBar.classList.toggle('is-hidden', isScrolled);
      document.body.classList.toggle('announcement-bar-closed', isScrolled);
    }

    setHeaderHeight();
  };

  let ticking = false;

  window.addEventListener('scroll', () => {
    if (!ticking) {
      requestAnimationFrame(() => {
        syncHeaderState();
        ticking = false;
      });
      ticking = true;
    }
  }, { passive: true });

  syncHeaderState();
})();

/* Transparent header
   ========================================================================== */
(function initTransparentHeader() {
  const header = document.querySelector('.site-header');
  const main = document.getElementById('MainContent');
  if (!header || !main) return;

  const enableOnHome       = header.dataset.transparentHome       === 'true';
  const enableOnHeroFirst  = header.dataset.transparentHeroFirst  === 'true';
  const enableOnCollection = header.dataset.transparentCollection === 'true';
  const enableOnProduct    = header.dataset.transparentProduct    === 'true';
  const enableOnBlog       = header.dataset.transparentBlog       === 'true';
  const enableOnPage       = header.dataset.transparentPage       === 'true';
  if (!enableOnHome && !enableOnHeroFirst && !enableOnCollection && !enableOnProduct && !enableOnBlog && !enableOnPage) return;

  const isHome       = document.body.classList.contains('template-index');
  const isCollection = document.body.classList.contains('template-collection');
  const isProduct    = document.body.classList.contains('template-product');
  const isBlog       = document.body.classList.contains('template-blog') || document.body.classList.contains('template-article');
  const isPage       = document.body.classList.contains('template-page');
  const firstSection = [...main.children].find((el) => el.nodeType === 1);
  const firstSectionIsHero = !!firstSection && (
    firstSection.matches('[data-overlay-header], .section-hero, .section-hero-video') ||
    firstSection.querySelector?.('[data-overlay-header], .section-hero, .section-hero-video')
  );

  const canBeTransparent =
    (enableOnHome       && isHome) ||
    (enableOnHeroFirst  && isHome && firstSectionIsHero) ||
    (enableOnCollection && isCollection) ||
    (enableOnProduct    && isProduct) ||
    (enableOnBlog       && isBlog) ||
    (enableOnPage       && isPage);
  if (!canBeTransparent) return;

  header.classList.add('can-be-transparent');
  document.body.classList.add('has-transparent-header');

  const syncTransparency = () => {
    const shouldBeTransparent = window.scrollY <= 10 && !header.classList.contains('search-active');
    header.classList.toggle('is-transparent', shouldBeTransparent);
  };

  syncTransparency();
  window.addEventListener('scroll', FLC.throttle(syncTransparency, 50), { passive: true });
  // scrollend fires when smooth-scroll finishes — catches cases where throttle
  // swallowed the final scroll event before scrollY reached 0
  window.addEventListener('scrollend', syncTransparency, { passive: true });
})();

/* Announcement bar auto-close
   ========================================================================== */
document.querySelectorAll('.announcement-bar__close').forEach(btn => {
  btn.addEventListener('click', () => {
    const bar = btn.closest('.announcement-bar');
    if (!bar) return;
    const section = bar.closest('.announcement-bar-section');

    const isMobile = window.innerWidth < 990;
    const isInFlow = !bar.classList.contains('announcement-bar--fixed');

    const emptyBar = () => {
      bar.remove();
      // Empty the section wrapper so CSS :empty selector can match
      if (section) section.innerHTML = '';
      document.body.classList.add('announcement-bar-closed');
      setHeaderHeight();
    };

    if (isMobile && isInFlow) {
      // Animate height collapse on mobile to prevent layout jump
      bar.style.overflow = 'hidden';
      bar.style.transition = 'height 0.3s ease, min-height 0.3s ease';
      bar.style.height = bar.offsetHeight + 'px';
      bar.style.minHeight = '0';
      requestAnimationFrame(() => {
        bar.style.height = '0';
        bar.addEventListener('transitionend', emptyBar, { once: true });
      });
    } else {
      emptyBar();
    }
  });
});

/* Search toggle
   ========================================================================== */
class SearchToggle extends HTMLElement {
  connectedCallback() {
    this.btn = this.querySelector('[data-search-toggle]');
    this.form = document.querySelector('.header-search');
    this.btn?.addEventListener('click', () => this.toggle());
    this.form?.querySelector('[data-search-close]')?.addEventListener('click', () => this.close());
    document.addEventListener('flc:drawers:close-all', () => this.close());
    document.addEventListener('keydown', (e) => { if (e.key === 'Escape') this.close(); });
  }

  toggle() { this.form?.classList.contains('is-open') ? this.close() : this.open(); }

  open() {
    this.form?.classList.add('is-open');
    this.btn?.setAttribute('aria-expanded', 'true');
    setTimeout(() => this.form?.querySelector('[data-search-input]')?.focus(), 100);
    document.querySelector('.site-header')?.classList.add('search-active');
  }

  close() {
    this.form?.classList.remove('is-open');
    this.btn?.setAttribute('aria-expanded', 'false');
    document.querySelector('.site-header')?.classList.remove('search-active');
  }
}

customElements.define('search-toggle', SearchToggle);

/* Video autoplay on scroll
   ========================================================================== */
const videoObserver = new IntersectionObserver((entries) => {
  entries.forEach(entry => {
    const video = entry.target;
    if (entry.isIntersecting) {
      video.play().catch(() => {});
    } else {
      video.pause();
    }
  });
}, { threshold: 0.25 });

document.querySelectorAll('video[data-autoplay]').forEach(v => videoObserver.observe(v));

/* Product Card Image Carousel
   ========================================================================== */
(function initProductCardCarousels() {
  function initCard(card) {
    const carousel = card.querySelector('[data-pc-carousel]');
    if (!carousel) return;

    const slides  = [...carousel.querySelectorAll('.product-card__carousel-slide')];
    const dots    = [...card.querySelectorAll('.product-card__carousel-dot')];
    const prevBtn = card.querySelector('[data-pc-prev]');
    const nextBtn = card.querySelector('[data-pc-next]');
    let current   = 0;

    function goTo(index) {
      const i = Math.max(0, Math.min(index, slides.length - 1));
      carousel.scrollLeft = carousel.clientWidth * i;
      dots.forEach((d, j) => d.classList.toggle('is-active', j === i));
      current = i;
    }

    prevBtn?.addEventListener('click', (e) => { e.preventDefault(); e.stopPropagation(); goTo(current - 1); });
    nextBtn?.addEventListener('click', (e) => { e.preventDefault(); e.stopPropagation(); goTo(current + 1); });

    // Click on carousel (not a button) — navigate to product
    const productUrl = carousel.dataset.pcUrl;
    carousel.addEventListener('click', (e) => {
      if (!e.target.closest('[data-pc-prev],[data-pc-next]') && productUrl) {
        window.location.href = productUrl;
      }
    });

    // ---- Touch (mobile): stop propagation to outer slider ----
    carousel.addEventListener('touchstart', (e) => { e.stopPropagation(); }, { passive: true });
    carousel.addEventListener('touchmove',  (e) => { e.stopPropagation(); }, { passive: true });
    carousel.addEventListener('touchend',   (e) => {
      e.stopPropagation();
      // Snap to nearest slide after native scroll settles
      setTimeout(() => {
        if (!carousel.clientWidth) return;
        const i = Math.round(carousel.scrollLeft / carousel.clientWidth);
        goTo(Math.max(0, Math.min(i, slides.length - 1)));
      }, 50);
    }, { passive: true });

    // Sync dots on any scroll
    carousel.addEventListener('scroll', FLC.debounce(() => {
      if (!carousel.clientWidth) return;
      const i = Math.round(carousel.scrollLeft / carousel.clientWidth);
      if (i !== current) { dots.forEach((d, j) => d.classList.toggle('is-active', j === i)); current = i; }
    }, 80), { passive: true });
  }

  // Init all cards already in DOM
  document.querySelectorAll('.product-card').forEach(initCard);

  // Init cards injected later (e.g. recommendations AJAX, quick-view)
  const observer = new MutationObserver(mutations => {
    mutations.forEach(m => {
      m.addedNodes.forEach(node => {
        if (node.nodeType !== 1) return;
        if (node.matches('.product-card')) { initCard(node); }
        else { node.querySelectorAll?.('.product-card').forEach(initCard); }
      });
    });
  });
  observer.observe(document.body, { childList: true, subtree: true });
})();

/* Unlock drawer / modal transitions after first paint
   ========================================================================== */
// Two nested rAF calls ensure we are past the first paint commit before
// adding the class that enables CSS transitions. This prevents all drawers
// (cart, filter, mobile menu) from playing their slide animation on every
// page load or refresh.
requestAnimationFrame(() => {
  requestAnimationFrame(() => {
    document.documentElement.classList.add('is-page-loaded');
  });
});

/* Reveal header (remove skeleton) once everything affecting its layout has
   settled: DOMContentLoaded guarantees every deferred script (including the
   announcement-bar slider) has run, and the two rAFs let layout/paint catch
   up with any style changes those scripts made.
   ========================================================================== */
function revealHeaderSkeleton() {
  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      document.documentElement.classList.add('header-ready');
    });
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', revealHeaderSkeleton);
} else {
  revealHeaderSkeleton();
}

/* Animate on scroll (simple)
   ========================================================================== */
if ('IntersectionObserver' in window) {
  const aoObserver = new IntersectionObserver((entries) => {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        entry.target.classList.add('is-visible');
        aoObserver.unobserve(entry.target);
      }
    });
  }, { threshold: 0.1, rootMargin: '0px 0px -40px 0px' });

  document.querySelectorAll('[data-animate]').forEach(el => aoObserver.observe(el));
}
